import "./AppAiTranslateAssist.scss";
import { StickyNote } from "../types";
import { t } from "../i18n";
import React, { useState, useEffect } from "react";
import { SingleValue, StylesConfig } from "react-select";
import { ConfigProvider, Input, message, Select } from 'antd';
import { Button } from "antd";
import { useAtom } from "jotai";
import { collabAPIAtom } from "../imago-app/collab/Collab";
import { IconLanguageSelectSwap } from "./icons";
import { nanoid } from "nanoid";
import { marked } from "marked";
import { StickyNoteBackground } from "./StickyNotePanel";
import {API_URL} from "../constants";

type LanguageOption = {
    label: string;
    value: string;
};

const languages: LanguageOption[] = [
    { label: "Arabic", value: "ar" },
    { label: "Afrikaans", value: "af" },
    { label: "Albanian", value: "sq" },
    { label: "Basque", value: "eu" },
    { label: "Belarusian", value: "be" },
    { label: "Bulgarian", value: "bg" },
    { label: "Catalan", value: "ca" },
    { label: "Chinese (Simplified)", value: "zh_CN" },
    { label: "Chinese (Traditional)", value: "zh_TW" },
    { label: "Croatian", value: "hr" },
    { label: "Czech", value: "cs" },
    { label: "Danish", value: "da" },
    { label: "Dutch", value: "nl" },
    { label: "English", value: "en" },
    { label: "Estonian", value: "et" },
    { label: "Filipino", value: "fil" },
    { label: "Finnish", value: "fi" },
    { label: "French", value: "fr" },
    { label: "Galician", value: "gl" },
    { label: "German", value: "de" },
    { label: "Greek", value: "el" },
    { label: "Haitian", value: "ht" },
    { label: "Hindi", value: "hi" },
    { label: "Hungarian", value: "hu" },
    { label: "Icelandic", value: "is" },
    { label: "Indonesian", value: "id" },
    { label: "Irish", value: "ga" },
    { label: "Italian", value: "it" },
    { label: "Japanese", value: "ja" },
    { label: "Korean", value: "ko" },
    { label: "Latin", value: "la" },
    { label: "Latvian", value: "lv" },
    { label: "Lithuanian", value: "lt" },
    { label: "Macedonian", value: "mk" },
    { label: "Malay", value: "ms" },
    { label: "Norwegian", value: "no" },
    { label: "Polish", value: "pl" },
    { label: "Portuguese (Brazil)", value: "pt_BR" },
    { label: "Portuguese (Portugal)", value: "pt_PT" },
    { label: "Romanian", value: "ro" },
    { label: "Russian", value: "ru" },
    { label: "Serbian", value: "sr" },
    { label: "Slovak", value: "sk" },
    { label: "Slovenian", value: "sl" },
    { label: "Spanish", value: "es" },
    { label: "Swahili", value: "sw" },
    { label: "Swedish", value: "sv" },
    { label: "Thai", value: "th" },
    { label: "Turkish", value: "tr" },
    { label: "Ukranian", value: "yk" },
    { label: "Vietlabelse", value: "vi" },
    { label: "Welsh", value: "cy" }
];

export const AppAiTranslateAssist: React.FC<{

}> = ({ }) => {
    const renderer = new marked.Renderer();
    const API_KEY = 'AIzaSyATBXajvzQLTDHEQbcpq0Ihe0vWDHmO520'

    const [text, setText] = useState<string>("");
    const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
    const [detectedLanguageName, setDetectedLanguageName] = useState<string | null>(null);
    const [sourceLanguage, setSourceLanguage] = useState<string>("auto");
    const [targetLanguage, setTargetLanguage] = useState<string>("en");
    const [translatedText, setTranslatedText] = useState<string>("");
    const [autoSourceLanguage, setAutoSourceLanguage] = useState<string | null>("");
    const [collabAPI] = useAtom(collabAPIAtom);

    const decodeHtmlEntities = (text: string): string => {
        const tempElement = document.createElement("textarea");
        tempElement.innerHTML = text;
        return tempElement.value;
    };

    const detectLanguage = async (inputText: string): Promise<string | null> => {
        if (!inputText.trim()) {
            setDetectedLanguage(null);
            setDetectedLanguageName(null);
            return null;
        }
        try {
            const response = await fetch(
                `${API_URL.aiHost}/aiapi/v1/reclanguage`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ prompt: inputText }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to detect language.");
            }
            if (!response.ok) throw new Error("Failed to detect language");

            const data = await response.json();

            let detectedLang = data.data.lang;

            const normalizationMap: Record<string, string> = {
                'en': 'en',
                'en-US': 'en',
                'en-GB': 'en',
                'ja': 'ja',
                'zh': 'zh_CN',
                'zh-CN': 'zh_CN',
                'zh-TW': 'zh_TW',
                'es': 'es',
                'fr': 'fr',
                'hi': 'hi',
                'it': 'it',
                'ms': 'ms',
                'pt': 'pt_BR',
                'pt-BR': 'pt_BR',
                'pt-PT': 'pt_PT',
            }

            const normalizedLang = normalizationMap[detectedLang];

            let detectedLangName = "";
            if (normalizedLang === "zh_CN"){
                detectedLangName = "Chinese (Simplified)";
            } else if (normalizedLang === "zh_TW") {
                detectedLangName = "Chinese (Traditional)";
            } else if (normalizedLang === "en") {
                detectedLangName = "English";
            } else if (normalizedLang === "pt_BR") {
                detectedLangName = "Portuguese (Brazil)";
            } else if (normalizedLang === "pt_PT") {
                detectedLangName = "Portuguese (Portugal)";
            } else if (normalizedLang === "ms") {
                detectedLangName = "Malay";
            }

            setDetectedLanguage(normalizedLang);
            setDetectedLanguageName(detectedLangName);
            return detectedLang;
        } catch (error) {
            console.error("Error detecting language:", error);
            return null;
        }
    };

    const languageOptions: LanguageOption[] = [
        {
            label: `${detectedLanguageName ? `${detectedLanguageName}` : "Auto Detected"}`,
            value: "auto",
        },
        ...languages,
    ];

    const translateText = async (
        inputText: string,
        sourceLang: string,
        targetLang: string
    ) => {
        if (!inputText.trim() || !sourceLang || !targetLang) {
            return;
        }
        try {
            const response = await fetch(
                "https://translate-pa.googleapis.com/v1/translateHtml",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json+protobuf",
                        "x-goog-api-key": API_KEY,
                    },
                    body: JSON.stringify([[inputText, sourceLang, targetLang], "te"]),
                }
            );
            if (!response.ok) {
                throw new Error("Failed to translate text.");
            }
            const data = await response.json();
            const rawTranslatedText = data[0][0];

            // Decode HTML entities
            const decodedText = decodeHtmlEntities(rawTranslatedText);

            setTranslatedText(decodedText);
        } catch (error) {
            console.error("Error translating text:", error);
        }
    };

    useEffect(() => {
        const autoTranslate = async () => {
            if (text.trim()) {
                const sourceLang =
                    sourceLanguage === "auto" ? await detectLanguage(text) : sourceLanguage;
                setAutoSourceLanguage(sourceLang);
                if (sourceLang && targetLanguage) {
                    translateText(text, sourceLang, targetLanguage);
                }
            } else {
                setTranslatedText("");
            }
        };

        autoTranslate();
    }, [text, sourceLanguage, targetLanguage]);

    const swapLanguages = () => {
        const tempSourceLang = sourceLanguage;

        setSourceLanguage(targetLanguage);
        if (autoSourceLanguage) {
            setDetectedLanguageName(autoSourceLanguage);
        }

        setTargetLanguage(autoSourceLanguage ? autoSourceLanguage : tempSourceLang);
        setText(translatedText);
        setTranslatedText(text);
        setAutoSourceLanguage("");

        if (text.trim()) {
            translateText(text, targetLanguage, sourceLanguage);
        } else {
            setTranslatedText("");
        }
    };

    const customStyles: StylesConfig = {
        control: (provided) => ({
            ...provided,
            fontSize: '8px',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: "center",
            border: '0',
            boxShadow: 'none',
            background: "#EFF0FA",
        }),
        valueContainer: (provided) => ({
            ...provided,
        }),
        singleValue: (provided) => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: "center",
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            display: 'none',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: 0,
            display: 'none'
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            alignItems: 'center',
        }),
        option: (provided) => ({
            ...provided,
        }),
    };



    return (
        <div className="app-ai-app-translateAssist">
            <ConfigProvider
                theme={{
                    token: {
                        colorBgContainer: '#f0f2f5',
                        colorBorder: '#1890ff',
                    },
                    components: {
                        Select: {
                            selectorBg: '#EFF0FA',
                            colorBorder: '#EFF0FA',
                        }
                    }
                }}
            >
                <div className="identify-section">

                    <div className="identify-section-select">
                        <div className="identify-section-flex">
                            <Select
                                showArrow={false}
                                style={{
                                    borderRadius: '4px'
                                }}
                                dropdownStyle={{
                                    textAlign: 'left',
                                }}

                                options={languageOptions.map((c: any) => { return { label: c.label, value: c.value } })}
                                onChange={(v) =>
                                    setSourceLanguage(v || "auto")
                                }

                                placeholder="Translate from"
                                value={sourceLanguage}
                            />
                        </div>

                        <div className="swap-box">
                            <button onClick={swapLanguages} className="swap-button">
                                {IconLanguageSelectSwap}
                            </button>
                        </div>


                        <div className="identify-section-flex">
                            <Select
                                showArrow={false}
                                options={languages.map((c: any) => { return { label: c.label, value: c.value } })}
                                onChange={(v) => {
                                    setTargetLanguage(v || "en")
                                }
                                }
                                placeholder="Translate to"
                                value={targetLanguage}
                            />
                        </div>
                    </div>
                </div>
                <div className="translate-form">
                    <div className="ai-translate-section">
                        <div className="translate-section-flex">
                            <div className="title">{t("labels.translate_detected_language")}</div>
                            <div className="origin-input-box">
                                <textarea
                                    value={text}
                                    onChange={(event) => setText(event.target.value)}
                                    placeholder={t("labels.enter_text")}
                                    rows={8}
                                    className="identify-textarea"
                                />

                                {text && (
                                    <button
                                        onClick={() => {
                                            setText("");
                                            setSourceLanguage("auto");
                                            setDetectedLanguage(null);
                                            setDetectedLanguageName(null);
                                        }}
                                        className="clear-button"
                                    >
                                        ✖
                                    </button>
                                )}
                            </div>

                        </div>



                        <div className="translate-text-section-flex ">
                            <div className="title">{t("labels.translate_tanslate_to")}</div>
                            <textarea
                                value={translatedText}
                                readOnly
                                placeholder={t("labels.translation")}
                                rows={8}
                                className="translate-textarea"
                            />
                        </div>

                    </div>
                    <div className="action">
                        <Button type="primary" onClick={() => {

                            const randomBackground = Math.floor(
                                (Math.random() * 10) / 2,
                            );

                            let originStickyContent = marked
                                .lexer(text)
                                .map((t) => {
                                    return marked.parse(t.raw, {
                                        ...marked.getDefaults(),
                                        gfm: true,
                                        breaks: true,
                                        renderer,
                                    }) as string;
                                })
                                .join("");

                            let translateStickyContent = marked
                                .lexer(translatedText)
                                .map((t) => {
                                    return marked.parse(t.raw, {
                                        ...marked.getDefaults(),
                                        gfm: true,
                                        breaks: true,
                                        renderer,
                                    }) as string;
                                })
                                .join("");

                            const stickyNote: StickyNote = {
                                id: nanoid(),
                                key: nanoid(),
                                status: "expand",
                                content: `<h4>The Original:</h4><p>${originStickyContent}</p><h4>Translation:</h4><p>${translateStickyContent}</p>`,
                                background:
                                    StickyNoteBackground[randomBackground],
                                fontStyle: "normal",
                                textDecoration: "none",
                                fontWeight: "normal",
                                width: 400,
                                height: 360,
                                position: null,
                            }
                            collabAPI?.addStickyNote(stickyNote)

                            // collabAPI?.addOriginTranslateText(text, translatedText)
                            // setAppState({
                            //     openSidebar: null,
                            // });
                        }} >{t("labels.btn_insert")}</Button>
                    </div>
                </div>

            </ConfigProvider>
        </div >
    );
}